import { useState } from "react";
import { Link } from "wouter";
import {
  ArrowLeft, Eye, FileText, Lock, MessageCircle, PackageCheck, Plus, Unlock, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { estadoDaConsulta } from "@/components/data/estado-consulta";
import { CompradorShell } from "@/components/mercado/produtor-shell";
import { ChipEstadoCarga, StatusChip } from "@/components/mercado/status-chip";
import { SelectDestino } from "@/components/mercado/select-destino";
import { PainelChamadaCarga } from "@/components/mercado/painel-chamada-carga";
import { usePode } from "@/lib/permissoes";
import { origemAtual, useNavegacao } from "@/lib/navegacao";
import { useToast } from "@/hooks/use-toast";
import {
  useAtualizarCarga, useCarga, useEstadoDaCarga, useRemoverItem,
} from "@/hooks/use-cargas";
import { useIncluirEmCarga, useRomaneios } from "@/hooks/use-romaneios";
import { fmtKgBR, fmtMoedaBR, fmtDiaCurto, fmtDataCurtaBR } from "@/lib/formato-mercado";
import type { CargaDetalheDTO, ItemDaCargaDTO } from "@shared/mercado-comprador";
import { cn } from "@/lib/utils";

/**
 * A MONTAGEM da carga — onde ela se monta e de onde ela sai.
 *
 * ⚠️ A CARGA É UMA SOMA, e a tela mostra isso: quanto já tem e quanto falta, de
 * quem veio cada romaneio, e o total por classificação — que é o que o cliente
 * quer saber antes de o caminhão chegar.
 *
 * ⚠️ SEM DADOS DE VENDA (D2). Preço ao cliente, margem e a receber ficam fora
 * desta fase. O único número em reais aqui é o VALOR PAGO AOS PRODUTORES (D6),
 * no rodapé do bloco "Por classificação" — só para ele, nunca no documento nem
 * no `/c/`.
 *
 * ⚠️ NADA DERIVADO É EDITÁVEL. kg, faltam e percentuais não têm campo: vêm do
 * servidor. Um campo de total editável ao lado de itens que somam é como o total
 * da venda passou a divergir dos itens no Cacho.
 *
 * ⚠️ FECHAR trava a composição e libera o documento — e o 409 do servidor é a
 * defesa de verdade; o `disabled` daqui é só a boa educação de não oferecer.
 */
export default function MercadoCarga({ id }: { id: string }) {
  const { voltar } = useNavegacao();
  const podeOperar = usePode("mercado.corretor.operar");
  const consulta = useCarga(id);
  const carga = consulta.data;

  const aguardando = estadoDaConsulta(consulta, { variante: "painel" });
  if (aguardando || !carga) return <CompradorShell>{aguardando}</CompradorShell>;

  const emFormacao = carga.status === "EM_FORMACAO";

  return (
    <CompradorShell className="space-y-4">
      <Cabecalho carga={carga} podeOperar={podeOperar} onVoltar={() => voltar("/mercado/cargas")} />

      {/* Duas colunas INDEPENDENTES, nunca grade 2×2: com grade, a coluna curta
          herda a altura da longa e sobra vão no meio da tela. */}
      <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr] lg:items-start">
        <div className="space-y-4 min-w-0">
          <BlocoProgresso carga={carga} />
          <RomaneiosNaCarga carga={carga} podeOperar={podeOperar} />
          {emFormacao && podeOperar && <AdicionarRomaneios carga={carga} />}
        </div>
        <div className="space-y-4 min-w-0">
          <PorClassificacao carga={carga} />
          <ClienteETransporte carga={carga} podeOperar={podeOperar} />
        </div>
      </div>
    </CompradorShell>
  );
}

// ─── Cabeçalho e ações de estado ─────────────────────────────────────────────

function Cabecalho({
  carga, podeOperar, onVoltar,
}: { carga: CargaDetalheDTO; podeOperar: boolean; onVoltar: () => void }) {
  const { toast } = useToast();
  const estado = useEstadoDaCarga(carga.id);
  const [entregando, setEntregando] = useState(false);
  const emFormacao = carga.status === "EM_FORMACAO";
  const entregue = carga.status === "ENTREGUE";

  function mudar(acao: "fechar" | "reabrir") {
    estado.mutate({ acao }, {
      onSuccess: () => toast({ title: acao === "fechar" ? "Carga fechada" : "Carga reaberta" }),
      // Nunca engolir: o 409 de "carga vazia" é justamente o que ele precisa ler.
      onError: (e: Error) => toast({
        title: acao === "fechar" ? "Não consegui fechar" : "Não consegui reabrir",
        description: e.message,
        variant: "destructive",
      }),
    });
  }

  return (
    <header className="space-y-2">
      <Button variant="ghost" size="sm" onClick={onVoltar} className="-ml-2" data-testid="button-voltar">
        <ArrowLeft className="h-4 w-4 mr-1" />Cargas
      </Button>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2.5 flex-wrap">
            <span className="font-mono">{carga.identificador}</span>
            <ChipEstadoCarga status={carga.status} />
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {carga.clienteNome
              ? `${carga.clienteNome}${carga.clienteLocal ? ` · ${carga.clienteLocal}` : ""}`
              : "Cliente a definir"}
            {carga.dataCarregamento && <> · {emFormacao ? "carregamento" : "carregou"} {fmtDiaCurto(carga.dataCarregamento)}</>}
            {carga.variedade ? ` · ${cap(carga.variedade)}` : " · Mista"}
          </p>
        </div>

        <div className="flex gap-2 flex-wrap">
          {emFormacao ? (
            <>
              {/* Em formação o documento existe como PRÉVIA: o envio fica
                  desabilitado lá dentro, com a razão escrita. */}
              <Button variant="outline" asChild data-testid="button-previa-documento">
                <Link href={`/mercado/cargas/${carga.id}/documento`} state={origemAtual()}>
                  <Eye className="h-4 w-4 mr-1.5" />Prévia do documento
                </Link>
              </Button>
              {podeOperar && (
                <Button
                  onClick={() => mudar("fechar")}
                  disabled={estado.isPending || carga.resumo.nRomaneios === 0}
                  data-testid="button-fechar-carga"
                >
                  <Lock className="h-4 w-4 mr-1.5" />Fechar carga
                </Button>
              )}
            </>
          ) : (
            <>
              {podeOperar && (
                <Button
                  variant="outline"
                  onClick={() => mudar("reabrir")}
                  disabled={estado.isPending || entregue}
                  title={entregue ? "Carga entregue não reabre: o cliente já recebeu." : undefined}
                  data-testid="button-reabrir-carga"
                >
                  <Unlock className="h-4 w-4 mr-1.5" />Reabrir
                </Button>
              )}
              {podeOperar && carga.status === "FECHADA" && (
                <Button variant="outline" onClick={() => setEntregando(true)} data-testid="button-marcar-entregue">
                  <PackageCheck className="h-4 w-4 mr-1.5" />Marcar entregue
                </Button>
              )}
              <Button asChild data-testid="button-documento">
                <Link href={`/mercado/cargas/${carga.id}/documento`} state={origemAtual()}>
                  <FileText className="h-4 w-4 mr-1.5" />Documento da carga
                </Link>
              </Button>
            </>
          )}
        </div>
      </div>

      {entregue && (
        <p className="text-xs text-muted-foreground">
          Carga entregue não reabre — o cliente já recebeu o documento e conferiu a carga.
        </p>
      )}

      <DialogEntrega carga={carga} aberta={entregando} onFechar={() => setEntregando(false)} />
    </header>
  );
}

/**
 * Marcar entregue PEDE o peso bruto quando não há.
 *
 * ⚠️ Pede, não exige: a balança do destino nem sempre fala no mesmo dia, e
 * travar a entrega por causa dela deixaria a carga presa em "fechada" — que é
 * pior do que uma entrega sem bruto, porque some do que está andando.
 */
function DialogEntrega({
  carga, aberta, onFechar,
}: { carga: CargaDetalheDTO; aberta: boolean; onFechar: () => void }) {
  const { toast } = useToast();
  const estado = useEstadoDaCarga(carga.id);
  const [bruto, setBruto] = useState(carga.resumo.kgBruto != null ? String(carga.resumo.kgBruto) : "");
  const [data, setData] = useState("");

  return (
    <Dialog open={aberta} onOpenChange={o => !o && onFechar()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Marcar como entregue</DialogTitle>
          <DialogDescription>
            O peso da balança do cliente, se ele já falou. A diferença para o líquido é informação, não erro.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="kg-bruto">Peso bruto (kg)</Label>
            <Input
              id="kg-bruto" type="number" inputMode="numeric" value={bruto}
              onChange={e => setBruto(e.target.value)} placeholder={String(carga.resumo.kgLiquido)}
              data-testid="input-kg-bruto"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="data-entrega">Entregue em</Label>
            <Input id="data-entrega" type="date" value={data} onChange={e => setData(e.target.value)} data-testid="input-data-entrega" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onFechar}>Cancelar</Button>
          <Button
            disabled={estado.isPending}
            onClick={() => estado.mutate(
              { acao: "entregue", kgBruto: bruto ? parseInt(bruto, 10) : null, entregueEm: data || null },
              {
                onSuccess: () => { onFechar(); toast({ title: "Carga entregue" }); },
                onError: (e: Error) => toast({ title: "Não consegui marcar", description: e.message, variant: "destructive" }),
              },
            )}
            data-testid="button-confirmar-entrega"
          >
            {estado.isPending ? "Salvando…" : "Marcar entregue"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Progresso ───────────────────────────────────────────────────────────────

function BlocoProgresso({ carga }: { carga: CargaDetalheDTO }) {
  const { resumo } = carga;
  const emFormacao = carga.status === "EM_FORMACAO";
  const pct = resumo.pctDoAlvo != null ? Math.min(100, Math.round(resumo.pctDoAlvo * 100)) : null;
  const [chamando, setChamando] = useState(false);

  return (
    <section className="rounded-lg border bg-card p-4 space-y-2.5" data-testid="bloco-progresso">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <span className="font-mono text-2xl font-semibold tabular-nums">
          {fmtKgBR(resumo.kgLiquido)}
          <span className="ml-1.5 text-[13px] font-medium text-muted-foreground font-sans">
            {emFormacao && carga.alvoKg ? `de ${fmtKgBR(carga.alvoKg)}` : "kg líquidos"}
          </span>
        </span>
        {emFormacao && resumo.faltam != null && resumo.faltam > 0 && (
          <span className="text-sm font-semibold text-[hsl(var(--warning,38_92%_40%))]">
            faltam {fmtKgBR(resumo.faltam)}
          </span>
        )}
        {!emFormacao && resumo.kgBruto != null && (
          <span className="text-[13px] text-muted-foreground tabular-nums">
            peso bruto {fmtKgBR(resumo.kgBruto)}
            {resumo.diferencaBruto != null && <> · diferença {fmtKgBR(resumo.diferencaBruto)}</>}
          </span>
        )}
      </div>

      {emFormacao && pct != null && (
        <>
          <div className="h-2 w-full rounded-full bg-muted overflow-hidden" aria-hidden>
            <div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
          </div>
          <div className="flex items-center justify-between gap-2 flex-wrap text-[12.5px] text-muted-foreground">
            <span>
              {pct}% do caminhão · {resumo.nProdutores} {resumo.nProdutores === 1 ? "produtor" : "produtores"}
              {" · "}{resumo.nRomaneios} {resumo.nRomaneios === 1 ? "romaneio" : "romaneios"}
            </span>
            <Button variant="ghost" size="sm" className="h-7" onClick={() => setChamando(true)} data-testid="button-contatar-produtores">
              <MessageCircle className="h-4 w-4 mr-1.5" />Contatar produtores
            </Button>
          </div>
        </>
      )}

      <PainelChamadaCarga cargaId={carga.id} aberta={chamando} onFechar={() => setChamando(false)} />
    </section>
  );
}

// ─── Romaneios na carga ──────────────────────────────────────────────────────

function ChipConferencia({ estado }: { estado: string | null }) {
  // `null` é registro próprio: não há conferência a fazer sobre o que ele mesmo
  // anotou, e um chip ali afirmaria um ato que ninguém praticou.
  if (estado == null) return <StatusChip kind="aberta">Registro próprio</StatusChip>;
  if (estado === "A_CONFERIR") return <StatusChip kind="warn">A conferir</StatusChip>;
  return <StatusChip kind="conf">{estado === "AJUSTADO" ? "Ajustado" : "Conferido"}</StatusChip>;
}

function RomaneiosNaCarga({ carga, podeOperar }: { carga: CargaDetalheDTO; podeOperar: boolean }) {
  const { abrir } = useNavegacao();
  const emFormacao = carga.status === "EM_FORMACAO";
  const colunas = carga.porClassificacao;

  return (
    <section className="rounded-lg border bg-card overflow-hidden" data-testid="romaneios-na-carga">
      <header className="px-4 py-2.5 border-b flex items-baseline justify-between gap-2">
        <h2 className="font-semibold text-sm flex items-center gap-1.5">
          <FileText className="h-4 w-4" />Romaneios na carga
        </h2>
        <span className="text-xs text-muted-foreground">
          {carga.resumo.nRomaneios} {carga.resumo.nRomaneios === 1 ? "romaneio" : "romaneios"}
          {" · "}{carga.resumo.nProdutores} {carga.resumo.nProdutores === 1 ? "produtor" : "produtores"}
        </span>
      </header>

      {carga.itens.length === 0 ? (
        <p className="p-6 text-center text-sm text-muted-foreground">
          Carga vazia. Inclua os romaneios que vão neste caminhão.
        </p>
      ) : (
        <>
          {/* ─── Celular: cartões ─────────────────────────────────────────
              A matriz de classificações não cabe em 390 px. No celular saem as
              colunas por classificação — o total por produtor é o que ele
              confere na beira do caminhão; o detalhe está no painel do romaneio. */}
          <ul className="md:hidden divide-y" data-testid="cartoes-itens">
            {carga.itens.map(item => (
              <li key={item.id}>
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => abrir(`/mercado/romaneios/${encodeURIComponent(item.ref)}`)}
                  onKeyDown={e => { if (e.key === "Enter") abrir(`/mercado/romaneios/${encodeURIComponent(item.ref)}`); }}
                  className="p-3 flex items-start justify-between gap-2 hover-elevate cursor-pointer"
                >
                  <div className="min-w-0">
                    <p className="font-semibold truncate">{item.produtorNome}</p>
                    <p className="text-xs text-muted-foreground truncate">{item.origem}</p>
                    <span className="inline-block mt-1"><ChipConferencia estado={item.conferencia} /></span>
                  </div>
                  <div className="shrink-0 text-right flex items-start gap-2">
                    <span className="font-mono font-semibold tabular-nums">{fmtKgBR(item.kg)}</span>
                    {emFormacao && podeOperar && <BotaoTirar cargaId={carga.id} item={item} />}
                  </div>
                </div>
              </li>
            ))}
          </ul>

          {/* ─── Desktop: a matriz ────────────────────────────────────────── */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm" data-testid="tabela-itens">
              <thead className="text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="text-left font-medium px-4 py-2">Produtor</th>
                  <th className="text-left font-medium px-2 py-2">Romaneio</th>
                  {colunas.map(c => (
                    <th key={c.chave} className="text-right font-medium px-2 py-2 whitespace-nowrap">{c.classificacao}</th>
                  ))}
                  <th className="text-right font-medium px-2 py-2 whitespace-nowrap">Total kg</th>
                  <th className="text-left font-medium px-2 py-2">Conferência</th>
                  {emFormacao && podeOperar && <th className="w-10" />}
                </tr>
              </thead>
              <tbody>
                {carga.itens.map(item => (
                  <tr
                    key={item.id}
                    onClick={() => abrir(`/mercado/romaneios/${encodeURIComponent(item.ref)}`)}
                    className="border-t cursor-pointer hover-elevate"
                    data-testid={`linha-item-${item.id}`}
                  >
                    <td className="px-4 py-2">
                      <b className="block truncate max-w-[160px]">{item.produtorNome}</b>
                      <span className="text-[11px] text-muted-foreground">
                        {item.noRizoma ? "Rizoma" : "Registro próprio"}
                      </span>
                    </td>
                    <td className="px-2 py-2">
                      <span className="font-mono text-xs">{item.numeroBloco ? `bloco ${item.numeroBloco}` : item.origem.replace("romaneio ", "")}</span>
                      <span className="block text-[11px] text-muted-foreground">{item.data ? fmtDiaCurto(item.data) : "—"}</span>
                    </td>
                    {colunas.map(c => (
                      <td key={c.chave} className="px-2 py-2 text-right font-mono tabular-nums">
                        {/* "—" e nunca "0": zero afirmaria que ele entregou nada
                            daquela classificação, e ele simplesmente não entregou. */}
                        {item.porClassificacao[c.chave] ? fmtKgBR(item.porClassificacao[c.chave]) : <span className="text-muted-foreground">—</span>}
                      </td>
                    ))}
                    <td className="px-2 py-2 text-right font-mono tabular-nums font-semibold">{fmtKgBR(item.kg)}</td>
                    <td className="px-2 py-2"><ChipConferencia estado={item.conferencia} /></td>
                    {emFormacao && podeOperar && (
                      <td className="px-2 py-2" onClick={e => e.stopPropagation()}>
                        <BotaoTirar cargaId={carga.id} item={item} />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-muted/30 font-semibold">
                <tr className="border-t">
                  <td className="px-4 py-2" colSpan={2}>Total</td>
                  {colunas.map(c => (
                    <td key={c.chave} className="px-2 py-2 text-right font-mono tabular-nums">{fmtKgBR(c.kg)}</td>
                  ))}
                  <td className="px-2 py-2 text-right font-mono tabular-nums" data-testid="total-kg-carga">
                    {fmtKgBR(carga.resumo.kgLiquido)}
                  </td>
                  <td colSpan={emFormacao && podeOperar ? 2 : 1} />
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

/**
 * Tirar da carga — NÃO apaga o romaneio (073).
 *
 * Ele volta para "Sem carga" na lista de Romaneios e segue em "A pagar". O texto
 * diz isso, porque antes ele sumia.
 */
function BotaoTirar({ cargaId, item }: { cargaId: string; item: ItemDaCargaDTO }) {
  const { toast } = useToast();
  const remover = useRemoverItem(cargaId);
  return (
    <Button
      variant="ghost"
      size="icon"
      className="h-7 w-7"
      disabled={remover.isPending}
      aria-label={`Tirar ${item.produtorNome} da carga`}
      title="Tirar da carga"
      onClick={() => remover.mutate(item.id, {
        onSuccess: () => toast({
          title: "Romaneio tirado da carga",
          description: item.natureza === "REGISTRO"
            ? "Ele continua na sua lista, sem carga."
            : "Ele volta para os romaneios disponíveis.",
        }),
        onError: (e: Error) => toast({ title: "Não consegui tirar da carga", description: e.message, variant: "destructive" }),
      })}
      data-testid={`button-tirar-${item.id}`}
    >
      <X className="h-4 w-4" />
    </Button>
  );
}

// ─── Adicionar romaneios ─────────────────────────────────────────────────────

function AdicionarRomaneios({ carga }: { carga: CargaDetalheDTO }) {
  const { toast } = useToast();
  const incluir = useIncluirEmCarga();
  const [marcados, setMarcados] = useState<Set<string>>(new Set());

  // Das DUAS origens, pela mesma espinha de Romaneios: a lista de "sem carga" da
  // tela de Romaneios e esta têm de dizer o mesmo número.
  const semCarga = useRomaneios({ periodo: "tudo", situacao: "SEM_CARGA" }, true);
  const disponiveis = semCarga.data?.linhas ?? [];
  const kgMarcado = disponiveis.filter(r => marcados.has(r.ref)).reduce((a, r) => a + r.kgBase, 0);
  const totalComSelecionados = carga.resumo.kgLiquido + kgMarcado;
  const acima = carga.alvoKg != null && totalComSelecionados > carga.alvoKg;

  return (
    <section className="rounded-lg border bg-card overflow-hidden" data-testid="adicionar-romaneios">
      <header className="px-4 py-2.5 border-b flex items-baseline justify-between gap-2">
        <h2 className="font-semibold text-sm flex items-center gap-1.5">
          <Plus className="h-4 w-4" />Adicionar romaneios
        </h2>
        <span className="text-xs text-muted-foreground">
          {disponiveis.length} sem carga
          {semCarga.data && <> · {fmtKgBR(semCarga.data.kpis.pendentes.semCarga.kg)}</>}
        </span>
      </header>

      {semCarga.isLoading ? (
        <p className="p-4 text-xs text-muted-foreground">Carregando os romaneios sem carga…</p>
      ) : semCarga.isError ? (
        // Estado de erro por BLOCO — nunca esqueleto eterno: a mensagem encerra
        // a espera, e o resto da tela continua útil.
        <p className="p-4 text-sm text-destructive">Não consegui carregar os romaneios sem carga.</p>
      ) : disponiveis.length === 0 ? (
        <p className="p-4 text-sm text-muted-foreground">
          Todos os romaneios já estão em algum caminhão.
        </p>
      ) : (
        <ul className="divide-y max-h-64 overflow-y-auto">
          {disponiveis.map(r => (
            <li key={r.ref}>
              <label className="flex items-center gap-2.5 px-4 py-2.5 text-sm cursor-pointer hover-elevate">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={marcados.has(r.ref)}
                  onChange={() => setMarcados(atual => {
                    const proximo = new Set(atual);
                    if (proximo.has(r.ref)) proximo.delete(r.ref);
                    else proximo.add(r.ref);
                    return proximo;
                  })}
                  data-testid={`check-adicionar-${r.ref}`}
                />
                <span className="min-w-0 flex-1">
                  <b>{r.produtor.nome}</b>
                  <span className="text-xs text-muted-foreground">
                    {" · "}{r.origem === "RIZOMA" ? r.numero : `bloco ${r.numero}`} · {fmtDiaCurto(r.data)}
                  </span>
                  {r.conferencia === "A_CONFERIR" && (
                    <span className="block mt-0.5"><StatusChip kind="warn">A conferir</StatusChip></span>
                  )}
                </span>
                <span className="font-mono font-semibold tabular-nums shrink-0">{fmtKgBR(r.kgBase)}</span>
              </label>
            </li>
          ))}
        </ul>
      )}

      <footer className="px-4 py-2.5 border-t flex items-center justify-between gap-2 flex-wrap text-xs text-muted-foreground">
        <span>
          {marcados.size > 0 ? (
            <>
              Com os selecionados: <b className="font-mono text-foreground">{fmtKgBR(totalComSelecionados)}</b>
              {acima && <> <StatusChip kind="warn">acima da capacidade</StatusChip></>}
            </>
          ) : "Marque os romaneios que vão neste caminhão."}
        </span>
        <span className="flex gap-2">
          <Button variant="outline" size="sm" asChild data-testid="button-registrar-romaneio">
            <Link href="/mercado/romaneios/novo" state={origemAtual()}>Registrar romaneio</Link>
          </Button>
          <Button
            size="sm"
            disabled={marcados.size === 0 || incluir.isPending}
            onClick={() => incluir.mutate(
              { refs: Array.from(marcados), cargaId: carga.id },
              {
                onSuccess: (r) => { setMarcados(new Set()); toast({ title: `${r.incluidos} ${r.incluidos === 1 ? "romaneio incluído" : "romaneios incluídos"}` }); },
                onError: (e: Error) => toast({ title: "Não consegui incluir", description: e.message, variant: "destructive" }),
              },
            )}
            data-testid="button-adicionar-selecionados"
          >
            Adicionar {marcados.size || ""}
          </Button>
        </span>
      </footer>
    </section>
  );
}

// ─── Por classificação ───────────────────────────────────────────────────────

function PorClassificacao({ carga }: { carga: CargaDetalheDTO }) {
  const maior = carga.porClassificacao.reduce((a, l) => (l.kg > a ? l.kg : a), 1);

  return (
    <section className="rounded-lg border bg-card overflow-hidden" data-testid="por-classificacao">
      <header className="px-4 py-2.5 border-b flex items-baseline justify-between gap-2">
        <h2 className="font-semibold text-sm">Por classificação</h2>
        <span className="text-xs text-muted-foreground">vai no documento</span>
      </header>

      {carga.porClassificacao.length === 0 ? (
        <p className="p-4 text-sm text-muted-foreground">Nada na carga ainda.</p>
      ) : (
        <ul className="p-4 space-y-2.5">
          {carga.porClassificacao.map((l, i) => (
            <li key={l.chave} className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-2 text-sm">
              <span className="min-w-0">
                <span className="block truncate">{l.classificacao}</span>
                {/* Largura proporcional ao MAIOR, não ao total: é o que deixa a
                    diferença entre as classificações visível quando uma domina. */}
                <span className="block h-1.5 rounded-full bg-muted overflow-hidden mt-1" aria-hidden>
                  <span
                    className="block h-full rounded-full"
                    style={{ width: `${(l.kg / maior) * 100}%`, background: `hsl(var(--chart-${(i % 5) + 1}))` }}
                  />
                </span>
              </span>
              <span className="font-mono tabular-nums">{fmtKgBR(l.kg)}</span>
              <span className="text-xs text-muted-foreground tabular-nums w-10 text-right">{Math.round(l.pct * 100)}%</span>
            </li>
          ))}
        </ul>
      )}

      {/* ⚠️ D6 — O ÚNICO NÚMERO EM REAIS DESTA TELA, e ele é só dele: o que a
          carga custou em romaneios. Nunca vai ao documento nem ao /c/, porque o
          cliente está do outro lado da negociação. */}
      <footer className="px-4 py-2.5 border-t flex items-baseline justify-between gap-2 text-sm">
        <span className="text-muted-foreground">Valor pago aos produtores</span>
        <b className="font-mono tabular-nums" data-testid="valor-pago-produtores">
          {fmtMoedaBR(carga.resumo.valorPagoProdutores)}
        </b>
      </footer>
    </section>
  );
}

// ─── Cliente e transporte ────────────────────────────────────────────────────

function ClienteETransporte({ carga, podeOperar }: { carga: CargaDetalheDTO; podeOperar: boolean }) {
  const [editando, setEditando] = useState(false);
  // Entregue trava a edição: mexer na placa de uma carga que já chegou é
  // reescrever o documento que o cliente recebeu.
  const editavel = podeOperar && carga.status !== "ENTREGUE";

  return (
    <section className="rounded-lg border bg-card overflow-hidden" data-testid="cliente-e-transporte">
      <header className="px-4 py-2.5 border-b flex items-center justify-between gap-2">
        <h2 className="font-semibold text-sm">Cliente e transporte</h2>
        {editavel && (
          <Button variant="ghost" size="sm" className="h-7" onClick={() => setEditando(true)} data-testid="button-editar-logistica">
            Editar
          </Button>
        )}
      </header>
      <dl className="p-4 grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-2 text-sm">
        <Campo rotulo="Cliente" valor={carga.clienteNome ?? "A definir"} />
        <Campo rotulo="Local" valor={carga.clienteLocal ?? "—"} />
        <Campo rotulo="Carregamento" valor={carga.dataCarregamento ? fmtDiaCurto(carga.dataCarregamento) : "—"} />
        <Campo rotulo="Placa" valor={carga.placa ?? "—"} mono />
        <Campo rotulo="Motorista" valor={carga.motoristaNome ?? "—"} />
        <Campo
          rotulo="Peso bruto"
          valor={carga.resumo.kgBruto != null ? `${fmtKgBR(carga.resumo.kgBruto)}` : "informe na pesagem"}
          mono={carga.resumo.kgBruto != null}
        />
      </dl>

      <DialogLogistica carga={carga} aberta={editando} onFechar={() => setEditando(false)} />
    </section>
  );
}

function Campo({ rotulo, valor, mono }: { rotulo: string; valor: string; mono?: boolean }) {
  return (
    <>
      <dt className="text-muted-foreground">{rotulo}</dt>
      <dd className={cn("min-w-0 truncate", mono && "font-mono")}>{valor}</dd>
    </>
  );
}

function DialogLogistica({
  carga, aberta, onFechar,
}: { carga: CargaDetalheDTO; aberta: boolean; onFechar: () => void }) {
  const { toast } = useToast();
  const atualizar = useAtualizarCarga(carga.id);
  const [clienteId, setClienteId] = useState<string | null>(carga.destinoContatoId);
  const [data, setData] = useState(carga.dataCarregamento ?? "");
  const [alvo, setAlvo] = useState(carga.alvoKg != null ? String(carga.alvoKg) : "");
  const [placa, setPlaca] = useState(carga.placa ?? "");
  const [motorista, setMotorista] = useState(carga.motoristaNome ?? "");
  const [telefone, setTelefone] = useState(carga.motoristaTelefone ?? "");

  return (
    <Dialog open={aberta} onOpenChange={o => !o && onFechar()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Cliente e transporte</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <SelectDestino valor={clienteId} onChange={setClienteId} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="edit-data">Carregamento</Label>
              <Input id="edit-data" type="date" value={data} onChange={e => setData(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-alvo">Capacidade (kg)</Label>
              <Input id="edit-alvo" type="number" inputMode="numeric" value={alvo} onChange={e => setAlvo(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-placa">Placa</Label>
              <Input id="edit-placa" value={placa} onChange={e => setPlaca(e.target.value)} className="font-mono" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-motorista">Motorista</Label>
              <Input id="edit-motorista" value={motorista} onChange={e => setMotorista(e.target.value)} />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="edit-telefone">Telefone do motorista</Label>
              <Input id="edit-telefone" inputMode="tel" value={telefone} onChange={e => setTelefone(e.target.value)} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onFechar}>Cancelar</Button>
          <Button
            disabled={atualizar.isPending}
            onClick={() => atualizar.mutate(
              {
                destinoContatoId: clienteId,
                dataCarregamento: data || null,
                alvoKg: alvo ? parseInt(alvo, 10) : null,
                placa: placa.trim() || null,
                motoristaNome: motorista.trim() || null,
                motoristaTelefone: telefone.trim() || null,
              },
              {
                onSuccess: () => { onFechar(); toast({ title: "Carga atualizada" }); },
                onError: (e: Error) => toast({ title: "Não consegui salvar", description: e.message, variant: "destructive" }),
              },
            )}
            data-testid="button-salvar-logistica"
          >
            {atualizar.isPending ? "Salvando…" : "Salvar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.substring(1).toLowerCase();
}
